import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { schema, uiSchema, translateSchema } from './config';
import BuilderForm from '../FormCarbon';
import NotFound from '../NotFound';
import DataTable from '../DataTable';
import { postTempConfigFile, setTempConfigFile } from '../../../actions';
import NestedDataTable from '../DataTable/components/NestedDataTable';
import funcTranslateHelper from '../../../i18next/funcTranslateHelper';
import { editAssetsProcess } from '../OnEdit/onEdit';
import { Modal } from 'carbon-components-react';
import { checkTransitionsAndActions } from './onDelete';

const Assets = props => {
  const { configFile, tempConfigFile, saveTempConfigFile } = props;

  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [affectedDependencies, setAffectedDependencies] = useState({});

  const config = tempConfigFile || configFile;
  const disableBuilder = config.disableBuilder;

  const assets = config.assetTypes || [];
  const permissions = config.permissions || [];
  const rows = assets.map(e => parseAsset(e));

  const headers = [
    { header: 'Nombre', key: 'name' },
    { header: 'Atributos', key: 'attributes' }
  ];

  const expandHeaders = [
    { header: 'Nombre', key: 'title' },
    { header: 'Tipo', key: 'type' },
    { header: 'Roles de vizualización', key: 'visualizationRoles' },
    { header: 'Obligatorio', key: 'required' }
  ];

  useEffect(() => {
    translateSchema(schema);
  }, []);

  function parseAsset (asset) {
    return {
      ...asset,
      attributes: asset.attributes.map(attr => attr.title)
    };
  }

  const toggleForm = () => {
    setOpen(!open);
    if (open) setFormData({});
  };

  const onAdd = ({ formData }) => {
    formData.id = new Date().getTime().toString();
    checkAttributes(formData);
    const formDataWithID = Object.assign({ ...formData }, { id: new Date().getTime().toString() });
    const configFile = {
      ...config,
      assetTypes: [...assets, formDataWithID],
      permissions: createPermission(formData)
    };
    saveConfig(configFile);
  };

  const onDelete = async id => {
    const aux = await checkTransitionsAndActions(config, id);
    setAffectedDependencies(aux);
    if (
      (aux.actionsAffected && aux.actionsAffected.length) ||
      (aux.transitionsAffected && aux.transitionsAffected.length)
    ) {
      setShowModal(true);
    } else {
      const configFile = {
        ...config,
        permissions: permissions.filter(perm => perm.resource !== id),
        assetTypes: config.assetTypes.filter(asset => asset.id !== id)
      };
      saveConfig(configFile);
    }
  };

  const onEdit = async ({ formData }) => {
    checkAttributes(formData);
    const newTempConfigFile = await editAssetsProcess(config, formData);
    newTempConfigFile.permissions = createPermission(formData);

    saveConfig(newTempConfigFile);
  };

  const checkAttributes = assetType => {
    assetType.attributes.forEach((attr, index) => {
      if ('listValue' in attr && attr.type !== 'array') delete attr.listValue;
      if ('assetType' in attr && attr.type !== 'asset') delete attr.assetType;
      if ('date' in attr && attr.type !== 'date-time') delete attr.date;
      if ('objSubtype' in attr && attr.type !== 'object') delete attr.subtype;
      if (
        'objSubtype' in attr &&
        attr.type === 'object' &&
        attr.objSubtype &&
        attr.objSubtype.objects &&
        attr.objSubtype.objects.length > 0
      ) {
        for (let index = 0; index < attr.objSubtype.objects.length; index++) {
          attr.objSubtype.objects[index].attrName = attr.objSubtype.objects[
            index
          ].title
            .toLowerCase()
            .replace(/\s/g, '');
        }
      }
      if (index === 0) {
        attr.attrName = 'name';
        attr.type = 'string';
        attr.required = true;
      } else {
        attr.attrName = attr.title.toLowerCase().replace(/\s/g, '');
      }
    });
  };

  const saveConfig = async config => {
    const res = await postTempConfigFile(config);
    if (res.message === 'OK') {
      saveTempConfigFile(config); //! !GUARDAR RESP.DATA en lugar de CONFIG cuando funcione la lambda
      setNotification({
        type: 'success',
        message: funcTranslateHelper('Builder.Message.realizado')
      });
    } else {
      setNotification({
        type: 'error',
        message: funcTranslateHelper('Builder.Message.error')
      });
    }

    setTimeout(() => setNotification(null), 2000);
    if (open) toggleForm();
  };

  const openEdit = async id => {
    const assetType = config.assetTypes.filter(asset => asset.id === id)[0];
    setFormData(assetType);
    toggleForm();
  };

  const createPermission = asset => {
    const roles = [
      ...new Set(asset.attributes.map(a => a.visualizationRoles).flat())
    ];

    const newPermissions = [];

    for (let l = 0; l < permissions.length; l++) {
      if (!(permissions[l].resource === asset.id)) {
        newPermissions.push(permissions[l]);
      }
    }

    for (const role in roles) {
      const attributesToAdd = [
        'name',
        'key',
        'tx_hash',
        'tx_timeStamp',
        'assetType',
        'tx_creationTimeStamp',
        'app',
        'channelID',
        'parent',
        'currentStage',
        'roles',
        'type',
        'user',
        'owner',
        'assetId'
      ];
      for (let h = 0; h < asset.attributes.length; h++) {
        if (asset.attributes[h].visualizationRoles.includes(roles[role])) {
          attributesToAdd.push(`${asset.attributes[h].attrName}`);
        }
      }

      newPermissions.push({
        action: 'read',
        attributes: attributesToAdd,
        role: roles[role],
        resource: asset.id
      });
    }
    return newPermissions;
  };

  if (!config.roles || !config.roles.length) {
    return (
      <NotFound
        title='Assets'
        subtitle='Subtitle'
        type='no-req'
        message={funcTranslateHelper('Builder:Assets:addRoles')}
      />
    );
  }

  const schemaRoles = config.roles.map(role => role.name);
  schema.definitions.roles.items.enumValues = schemaRoles;
  schema.definitions.roles.items.enum = schemaRoles;

  const enumAssetType = [''];
  const enumNamesAssetType = [funcTranslateHelper('Builder:Assets:form:assetSelect')];

  config.assetTypes.forEach((level1, index) => {
    enumNamesAssetType.push(level1.name);
    enumAssetType.push(level1.id);
  });

  schema.definitions.attribute.dependencies.type.oneOf[3].properties.assetType.enum = enumAssetType;
  schema.definitions.attribute.dependencies.type.oneOf[3].properties.assetType.enumNames = enumNamesAssetType;

  if (open) {
    return (
      <BuilderForm
        onAdd={onAdd}
        onClose={toggleForm}
        onEdit={onEdit}
        open={open}
        title={funcTranslateHelper('Builder.Assets.form.titulo')}
        subtitle={funcTranslateHelper('Builder.Assets.form.subtitulo')}
        formData={formData}
        schema={schema}
        uiSchema={uiSchema}
        Template='Asset'
        primaryButtonText={funcTranslateHelper('Guardar')}
        secondaryButtonText={funcTranslateHelper('Cancelar')}
      />
    );
  }

  if (showModal) {
    return (
      <Modal
        className='some-class'
        hasScrollingContent={false}
        iconDescription={funcTranslateHelper(
          'Builder.Assets.modal.iconDescription'
        )}
        modalAriaLabel='A label to be read by screen readers on the modal root node'
        modalHeading={funcTranslateHelper('Builder.Assets.modal.title')}
        onRequestClose={() => {
          setAffectedDependencies({});
          setShowModal(false);
        }}
        open
        passiveModal
        primaryButtonDisabled={false}
        selectorPrimaryFocus='[data-modal-primary-focus]'
        size={undefined}
      >
        <div className='bx--modal-content__text'>
          {funcTranslateHelper('Builder.Assets.modal.message')}
          <br />
          {affectedDependencies.actionsAffected && affectedDependencies.actionsAffected.length > 0 && (
            <>
              <br />
              <strong>
                {funcTranslateHelper('Builder.Assets.modal.actionsTitle')}
              </strong>
              <br />
              <ul>
                {affectedDependencies.actionsAffected.map((item, key) => (
                  <li key={key}>&emsp; - {item.name}</li>
                ))}
              </ul>
            </>
          )}
          {affectedDependencies.transitionsAffected && affectedDependencies.transitionsAffected.length > 0 && (
            <>
              <br />
              <strong>
                {funcTranslateHelper('Builder.Assets.modal.transitionTitle')}
              </strong>
              <br />
              <ul>
                {affectedDependencies.transitionsAffected.map((item, key) => (
                  <li key={key}>&emsp; - {item.nombre}</li>
                ))}
              </ul>
            </>
          )}
          <br />
          {funcTranslateHelper(
            'Builder.Assets.modal.callToActionMessage'
          )}
          <br />
        </div>
      </Modal>
    );
  }

  if (!rows.length) {
    return (
      <NotFound
        title={funcTranslateHelper('Builder.Assets.titulo')}
        subtitle={funcTranslateHelper('Builder.Assets.subtitulo')}
        handleOpen={toggleForm}
        notification={notification}
        message={funcTranslateHelper('Builder:Assets:newAssets')}
      />
    );
  }

  return (
    <div className='data-table-container'>
      <DataTable
        title={funcTranslateHelper('Builder.Assets.titulo')}
        subtitle={funcTranslateHelper('Builder.Assets.subtitulo')}
        headers={headers}
        rows={rows}
        handleOpen={toggleForm}
        onDelete={onDelete}
        onEdit={openEdit}
        allowEdit={!disableBuilder}
        rawData={assets}
        hasExpandableRows
        renderExpandable={id => (
          <NestedDataTable
            key={id}
            id={id}
            rawData={assets}
            headers={expandHeaders}
            keyName='attributes'
          />
        )}
        notification={notification}
      />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    configFile: state.configFile,
    tempConfigFile: state.tempConfigFile
  };
};

const mapDispatchToProps = dispatch => {
  return {
    saveTempConfigFile: data => dispatch(setTempConfigFile(data))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Assets);
